import logger from '@/utils/client-logger';
import { env } from '@/utils/env';
import { Provider, ProviderType, TrackingEvent } from '../types';

const OPENAI_PIXEL_SDK_ID = 'openai-ads-pixel-sdk';
const OPENAI_PIXEL_SDK_URL = 'https://bzrcdn.openai.com/sdk/oaiq.min.js';
type OaiqFunction = NonNullable<Window['oaiq']>;

interface OpenAiPixelConfig {
  pixelId: string;
  isEnabled?: boolean;
}

export class OpenAiPixelProvider implements Provider {
  type = ProviderType.OPENAI;
  isEnabled: boolean;
  loggingEnabled = false;
  private initialized = false;
  private readonly config: OpenAiPixelConfig;

  constructor(config: OpenAiPixelConfig) {
    this.config = config;
    this.isEnabled = config.isEnabled ?? true;
    this.loggingEnabled = env.NEXT_PUBLIC_ENV === 'local';
  }

  async init(): Promise<boolean> {
    try {
      if (typeof window === 'undefined' || typeof document === 'undefined') return false;
      if (this.initialized) return true;
      if (!this.config.pixelId) {
        logger.warn('OpenAI Pixel: missing Pixel ID');
        return false;
      }

      const oaiq = this.ensureSdkQueue();
      const initializedPixelIds =
        window.__OPENAI_ADS_INITIALIZED_PIXEL_IDS__ ??
        (window.__OPENAI_ADS_INITIALIZED_PIXEL_IDS__ = new Set<string>());

      if (!initializedPixelIds.has(this.config.pixelId)) {
        oaiq('init', { pixelId: this.config.pixelId });
        initializedPixelIds.add(this.config.pixelId);
      }

      this.initialized = true;
      return true;
    } catch (error: unknown) {
      logger.error('Failed to initialize OpenAI Pixel:', error);
      return false;
    }
  }

  track(event: TrackingEvent): void {
    if (!this.initialized || typeof window === 'undefined') return;
    if (!event.eventName || !event.properties || typeof event.properties !== 'object') {
      logger.warn('OpenAI Pixel: invalid tracking event', event);
      return;
    }

    try {
      const oaiq = window.oaiq;
      if (!oaiq) return;
      if (this.loggingEnabled) logger.log('OpenAI Pixel tracking event:', event);

      if (event.eventId) {
        oaiq('measure', event.eventName, event.properties, { event_id: event.eventId });
        return;
      }
      oaiq('measure', event.eventName, event.properties);
    } catch (error: unknown) {
      logger.error('OpenAI Pixel tracking error:', error);
    }
  }

  isInitialized(): boolean {
    return this.initialized;
  }

  enableLog(): void {
    this.loggingEnabled = true;
  }

  disableLog(): void {
    this.loggingEnabled = false;
  }

  private ensureSdkQueue(): OaiqFunction {
    if (window.oaiq) return window.oaiq;

    const queue = ((command: string, ...args: unknown[]) => {
      queue.q?.push([command, ...args]);
    }) as OaiqFunction;
    queue.q = [];
    window.oaiq = queue;

    const script = document.createElement('script');
    script.id = OPENAI_PIXEL_SDK_ID;
    script.async = true;
    script.src = OPENAI_PIXEL_SDK_URL;
    script.onerror = () => logger.error('OpenAI Pixel: failed to load SDK');

    const firstScript = document.getElementsByTagName('script')[0];
    if (firstScript?.parentNode) firstScript.parentNode.insertBefore(script, firstScript);
    else document.head.appendChild(script);

    return queue;
  }
}
